/**
 * useArticleIndexOptions — bridge between the firebase-core read services and the ArticleIndex
 * settings panel: the project's article folders and topics (id + a label in the editor locale).
 * Imperative load on project change (no listeners); a failed read leaves empty lists and an
 * `error`, so the panel stays usable (the filter selects just have nothing to offer).
 */
import { useEffect, useMemo, useState } from 'react';
import { ArticleFolderService } from '@modules/firebase-core/services/ArticleFolderService';
import { ArticleSiteSettingsReadService, type ArticleSiteTopic } from '@modules/firebase-core/services/ArticleSiteSettingsReadService';

export interface ArticleIndexFolderOption {
  id: string;
  name: string;
}

export interface ArticleIndexTopicOption {
  id: string;
  label: string;
}

export interface ArticleIndexOptions {
  folders: ArticleIndexFolderOption[];
  topics: ArticleIndexTopicOption[];
  loading: boolean;
  error: string | null;
}

const EMPTY: ArticleIndexOptions = { folders: [], topics: [], loading: false, error: null };

/** exact locale → base language → en → first non-empty label (same order the publish side uses). */
export function pickTopicLabel(label: Record<string, string>, locale: string): string {
  const candidates = [label[locale], label[locale.split('-')[0]], label.en, ...Object.values(label)];
  const found = candidates.find((text) => typeof text === 'string' && text.trim());
  return found ? found.trim() : '';
}

interface LoadedOptions {
  projectId: string;
  folders: ArticleIndexFolderOption[];
  topics: ArticleSiteTopic[];
  error: string | null;
}

export function useArticleIndexOptions(projectId: string | undefined, locale: string): ArticleIndexOptions {
  const folderService = useMemo(() => new ArticleFolderService(), []);
  const settingsService = useMemo(() => new ArticleSiteSettingsReadService(), []);
  const [loaded, setLoaded] = useState<LoadedOptions | null>(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    (async () => {
      let next: LoadedOptions;
      try {
        const [folders, topics] = await Promise.all([folderService.list(projectId), settingsService.readTopics(projectId)]);
        next = { projectId, folders: folders.map((folder) => ({ id: folder.id, name: folder.name })), topics, error: null };
      } catch (error) {
        next = { projectId, folders: [], topics: [], error: error instanceof Error ? error.message : String(error) };
      }
      if (!cancelled) setLoaded(next);
    })();
    return () => { cancelled = true; };
  }, [projectId, folderService, settingsService]);

  return useMemo(() => {
    if (!projectId) return EMPTY;
    if (!loaded || loaded.projectId !== projectId) return { ...EMPTY, loading: true };
    return {
      folders: loaded.folders,
      topics: loaded.topics.map((topic) => ({ id: topic.id, label: pickTopicLabel(topic.label, locale) || topic.id })),
      loading: false,
      error: loaded.error,
    };
  }, [projectId, loaded, locale]);
}
